// eslint-disable-next-line eag/no-external-date-time-format-functions -- TODO [T20-67958]
import {format, isValid} from 'date-fns';
import {Button, ColorSchemeType, Flag, Parameter, Parameters} from 'platform/components';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Show,
  Space,
  StorageImage,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {ReactElement, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  InterestVehicleData,
  NewOfferPurchaseBrokerageVehicleResponseBody,
  NewOfferPurchaseVehicleResponseBody,
  OfferSaleVehicleResponseBody,
} from '@dms/api/finance';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {FullVehicle, VehicleDataResponseBody, VehicleTypeEnumObject} from '@dms/api/shared';
import {useGetSaleVehicleFromSaleVehicleIdQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  getVehicleCoverPhoto,
  thumbnailUrl,
  usePricePermissions,
  useVehicleConditionEvaluation,
  useVehiclePhotosPermissions,
  VehicleConditionEvaluation,
} from '@dms/shared';

import {
  buildArray,
  composePath,
  openInNewTab,
  parseDate,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {useVehicleTitle} from '../../hooks/useVehicleTitle';
import {
  selectBodyStyle,
  selectDrive,
  selectFuelType,
  selectTransmission,
} from '../../store/vehicleCatalogue/selectors';
import {SimilarVehicle} from '../../types/SimilarVehicle';
import {PriceBox} from '../PriceBox/PriceBox';

type FlagType = {
  name: string;
  color: ColorSchemeType;
};

type VehicleWidgetType = {
  // TODO: Create custom interface with only subset of fields
  vehicle?: Partial<FullVehicle | InterestVehicleData | SimilarVehicle | VehicleDataResponseBody>;
  extraControls?: ReactElement;
  flags?: Array<FlagType>;
  shouldShowDetailButton?: boolean;
  shouldShowPrice?: boolean;
  shouldShowCondition?: boolean;
  extraFlags?: ReactElement;
  purchaseVehicle?: NewOfferPurchaseVehicleResponseBody;
  purchaseBrokeraggeVehicle?: NewOfferPurchaseBrokerageVehicleResponseBody;
  selling?: OfferSaleVehicleResponseBody;
  showMileagePurchase?: boolean;
  showMileageSale?: boolean;
  saleVehicleId?: string;
} & TestIdProps;

export const VehicleWidget = (props: VehicleWidgetType) => {
  const {
    vehicle,
    extraControls,
    flags,
    shouldShowDetailButton = true,
    shouldShowPrice = true,
    shouldShowCondition = true,
    purchaseVehicle,
    purchaseBrokeraggeVehicle,
    selling,
    saleVehicleId,
  } = props;

  const castedVehicle = vehicle as FullVehicle;

  const {vin, id, isArchived} = castedVehicle ?? {};
  const {manufacturedOn} = castedVehicle?.vehicle ?? {};
  const {firstRegistrationOn} = castedVehicle?.vehicle ?? {};

  const registrationPlate =
    castedVehicle?.registrationPlate || castedVehicle?.vehicle?.registrationPlate;

  const [vehicleConditionEvaluation] = useVehicleConditionEvaluation(id);

  const fuelType = useSelector(
    selectFuelType(vehicle?.fuelType, vehicle?.type as VehicleTypeEnumObject)
  );
  const transmission = useSelector(
    selectTransmission(vehicle?.transmission, vehicle?.type as VehicleTypeEnumObject)
  );
  const bodyStyle = useSelector(
    selectBodyStyle(vehicle?.bodyStyle, vehicle?.type as VehicleTypeEnumObject)
  );
  const driveType = useSelector(
    selectDrive(vehicle?.drive, vehicle?.type as VehicleTypeEnumObject)
  );

  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();
  const getVehicleTitle = useVehicleTitle(vehicle);

  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId: id,
  });

  const coverPhoto = useMemo(
    () => getVehicleCoverPhoto((vehicle as FullVehicle)?.photos ?? []),
    [vehicle]
  );

  const {canReadVehicleSellingPrice, canReadVehiclePurchasePrice} = usePricePermissions({
    vehicleRecordId: castedVehicle.id,
    businessCaseRecordId: null,
  });
  const canReadPrices = canReadVehicleSellingPrice && canReadVehiclePurchasePrice;
  const params = useParams();

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: params?.id ?? ''},
    {skip: isNilOrEmpty(params?.id)}
  );
  const businessCaseType = businessCase?.businessCaseInternalType;

  const {data: saleVehicle} = useGetSaleVehicleFromSaleVehicleIdQuery(
    {
      vehicleId: saleVehicleId ?? '',
    },
    {skip: isNilOrEmpty(saleVehicleId)}
  );

  const vehicleInformation = buildArray<Parameter>()
    .add({
      title: isNotNilOrEmpty(vin) ? vin : i18n.t('entity.vehicle.notifications.noVIN'),
      tooltip: i18n.t('entity.vehicle.labels.vin'),
      variant: 'secondary',
    })
    .add({
      title: isNotNilOrEmpty(registrationPlate)
        ? registrationPlate
        : i18n.t('entity.vehicle.notifications.noLicencePlate'),
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
      variant: 'secondary',
    })
    .whenNotNil(
      isNotNil(manufacturedOn) && isValid(parseDate(manufacturedOn))
        ? {
            title: format(parseDate(manufacturedOn), 'yyyy'),
            tooltip: i18n.t('entity.vehicle.labels.realProductionYear'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      isNotNil(firstRegistrationOn) && isValid(parseDate(firstRegistrationOn))
        ? {
            title: formatDateTime('dateShort', parseDate(firstRegistrationOn)),
            tooltip: i18n.t('entity.vehicle.labels.firstRegistration'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      props.showMileagePurchase && isNotNilOrEmpty(saleVehicle?.purchaseMileage)
        ? {
            title: `${formatNumber(saleVehicle?.purchaseMileage, 0)} ${i18n.t('general.metric.km')}`,
            tooltip: i18n.t('entity.vehicle.labels.mileage'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      props.showMileageSale && isNotNilOrEmpty(saleVehicle?.saleMileage)
        ? {
            title: `${formatNumber(saleVehicle?.saleMileage, 0)} ${i18n.t('general.metric.km')}`,
            tooltip: i18n.t('entity.vehicle.labels.mileage'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      transmission?.label
        ? {
            title: transmission.label,
            tooltip: i18n.t('entity.vehicle.labels.transmission'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      fuelType?.label
        ? {
            title: fuelType.label,
            tooltip: i18n.t('entity.vehicle.labels.fuel'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      bodyStyle?.label
        ? {
            title: bodyStyle.label,
            tooltip: i18n.t('entity.vehicle.labels.body'),
            variant: 'secondary',
          }
        : null
    )
    .whenNotNil(
      driveType?.label
        ? {
            title: driveType.label,
            tooltip: i18n.t('entity.vehicle.labels.drive'),
            variant: 'secondary',
          }
        : null
    );

  const foreignPrice =
    businessCaseType === 'SELLING'
      ? saleVehicle?.sellingPriceIsForeignCurrency
      : saleVehicle?.purchasePriceIsForeignCurrency;

  const isNotPurchaseBrokerage = businessCaseType !== 'PURCHASE_BROKERAGE';

  const isFullWidth =
    !shouldShowDetailButton && !(shouldShowPrice && canReadPrices) && isNilOrEmpty(extraControls);

  const priceContent = shouldShowPrice && canReadPrices && (
    <HStack justify="flex-end" align="center">
      <PriceBox
        data-testid={suffixTestId(`sellingPrice`, props)}
        align="right"
        isVatDeductible={
          purchaseVehicle?.buying?.vatDeductible ??
          purchaseBrokeraggeVehicle?.pricing?.vatDeductible ??
          selling?.vatDeductible
        }
        price={
          purchaseVehicle?.buying?.buyingPrice?.priceWithVat ??
          purchaseVehicle?.buying?.buyingPrice?.priceNotDeductible ??
          purchaseBrokeraggeVehicle?.pricing?.sellingPrice?.priceWithVat ??
          purchaseBrokeraggeVehicle?.pricing?.sellingPrice?.priceNotDeductible ??
          selling?.sellingPrice?.withVat
        }
        priceSub={
          purchaseVehicle?.buying?.buyingPrice?.priceWithoutVat ??
          purchaseBrokeraggeVehicle?.pricing?.sellingPrice?.priceWithoutVat ??
          selling?.sellingPrice?.withoutVat
        }
        foreignPrice={isNotPurchaseBrokerage ? foreignPrice : null}
      />
    </HStack>
  );

  const actionsContent = (
    <HStack spacing={2} align="center" justify="flex-end">
      {extraControls}
      {shouldShowDetailButton && (
        <Button
          data-testid={suffixTestId(`buttonDetail`, props)}
          variant="outlined"
          leftIcon="action/launch"
          onClick={() => {
            if (id) {
              openInNewTab(composePath(vehiclesRoutes.detail, {params: {id}}));
            }
          }}
          title={i18n.t('entity.vehicle.labels.detail')}
        />
      )}
    </HStack>
  );

  const vehicleFlagsContent = (
    <HStack data-testid={suffixTestId(`flags`, props)} spacing={1}>
      <Show when={props.extraFlags}>{props.extraFlags}</Show>
      <Show when={flags?.length}>
        {flags?.map((flag, index) => (
          <Flag
            key={flag.name}
            data-testid={suffixTestId(`flag[${index}]`, props)}
            size="small"
            colorScheme={flag.color}
            label={flag.name}
          />
        ))}
      </Show>
    </HStack>
  );

  const vehicleTitleParamsContent = (
    <>
      <HStack align="center">
        <Heading size={4} data-testid={suffixTestId(`title`, props)}>
          {(vehicle as FullVehicle)?.title ?? getVehicleTitle}
        </Heading>
      </HStack>
      <HStack spacing={1} wrap>
        <Parameters
          parameters={vehicleInformation}
          size="small"
          color="secondary"
          data-testid={suffixTestId(`parameters`, props)}
          appendix={
            shouldShowCondition && (
              <VehicleConditionEvaluation
                size="small"
                color="secondary"
                vehicleCondition={vehicleConditionEvaluation}
                data-testid={suffixTestId(`overallConditions`, props)}
              />
            )
          }
        />
      </HStack>
    </>
  );

  return (
    <>
      <Show onMobile>
        <div data-testid={props['data-testid']}>
          <VStack spacing={3}>
            {/* Unsupported property in Box */}
            {/* eslint-disable-next-line react/forbid-dom-props */}
            <div style={{filter: isArchived ? 'grayscale(100%)' : undefined}}>
              <Box
                borderRadius="small"
                overflow="hidden"
                width="100%"
                height={50}
                data-testid={suffixTestId(`image`, props)}
              >
                <StorageImage
                  shouldUseOriginal={!coverPhoto || !canReadVehicleAlbumPhotos}
                  url={canReadVehicleAlbumPhotos ? (coverPhoto?.url ?? thumbnailUrl) : thumbnailUrl}
                  width="100%"
                  height={50}
                  fit="contain"
                />
              </Box>
            </div>
            {vehicleFlagsContent}
            <HStack spacing={3} align="flex-start">
              <Box flex={1}>{vehicleTitleParamsContent}</Box>
              {priceContent}
            </HStack>
            {actionsContent}
          </VStack>
        </div>
      </Show>
      <Show onTablet onNotebook onDesktop>
        <Grid columns={12} data-testid={props['data-testid']}>
          <GridItem span={isFullWidth ? 12 : [6, 6, 6, 6, 8]}>
            <HStack spacing={0}>
              {/* Unsupported property in Box */}
              {/* eslint-disable-next-line react/forbid-dom-props */}
              <div style={{filter: isArchived ? 'grayscale(100%)' : undefined}}>
                <Box
                  borderRadius="small"
                  overflow="hidden"
                  width={25}
                  height={18}
                  data-testid={suffixTestId(`image`, props)}
                >
                  <StorageImage
                    shouldUseOriginal={!coverPhoto || !canReadVehicleAlbumPhotos}
                    url={
                      canReadVehicleAlbumPhotos ? (coverPhoto?.url ?? thumbnailUrl) : thumbnailUrl
                    }
                    width={25}
                    height={18}
                    fit="cover"
                  />
                </Box>
              </div>
              <Box flex={1} paddingLeft={4}>
                {vehicleFlagsContent}
                <Space vertical={1} />
                {vehicleTitleParamsContent}
              </Box>
            </HStack>
          </GridItem>
          <GridItem span={[6, 6, 6, 6, 4]}>
            <VStack height="100%" justify="space-between">
              {actionsContent}
              {priceContent}
            </VStack>
          </GridItem>
        </Grid>
      </Show>
    </>
  );
};
